import { DateObject } from "react-multi-date-picker";
import { NA } from "./Constant";

type ReportDate = Date | DateObject | string | null | undefined;

const parseReportDate = (value: ReportDate): Date | null => {
  if (!value || value === NA) return null;
  if (value instanceof DateObject) return value.toDate();
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // API dates describe a calendar day; do not shift it when parsing a timestamp.
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/);
  const display = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (iso || display) {
    const year = Number(iso ? iso[1] : display![3]);
    const month = Number(iso ? iso[2] : display![2]);
    const day = Number(iso ? iso[3] : display![1]);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
      ? date : null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatReportDate = (value: ReportDate, ..._options: unknown[]): string => {
  const date = parseReportDate(value);
  if (!date) return NA;
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
};

export const getReportDay = (value: ReportDate): string => {
  const date = parseReportDate(value);
  return date ? date.toLocaleDateString("en-GB", { weekday: "long" }) : "";
};

export const isReportDateHeader = (header: string): boolean => {
  const label = header.trim().replaceAll("_", " ");
  if (/\bday$|\bdate range\b/i.test(label)) return false;
  return /\bdate\b|^doj$|^dob$|^anniversary$|^submitted(?:\s+on)?$/i.test(label);
};

// Keep day columns beside their dates, including reports with several date fields.
export const prepareReportExportRows = (rows: Record<string, any>[]): Record<string, any>[] => {
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  const dateHeaders = headers.filter(isReportDateHeader);
  const dayHeaders = dateHeaders.map((header, index) => index === 0 ? "Day" : `${header} Day`);
  return rows.map((row) => {
    const result: Record<string, any> = {};
    headers.forEach((header) => {
      if (dayHeaders.includes(header)) return;
      const value = row[header];
      result[header] = value;
      if (!isReportDateHeader(header)) return;
      result[header] = getReportDay(value) ? formatReportDate(value) : "";
      const dayHeader = dateHeaders[0] === header ? "Day" : `${header} Day`;
      result[dayHeader] = getReportDay(value);
    });
    return result;
  });
};
